import { useEffect, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { summaryApi, transactionApi, assetApi, categoryApi, goldApi, silverApi, bankSavingApi } from '../lib/api';
import type { Asset, AssetBalanceHistory, BankSavingContract, Category, GoldPnlBreakdown, MonthlySummary, NetWorthPoint, RangeSummary, SilverPnlBreakdown, Transaction } from '@asset-mgmt/shared';
import { useAuth } from '../context/AuthContext';

const CHART_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316', '#84cc16'];

const ASSET_TYPE_COLORS: Record<string, string> = {
  cash:         '#10b981',
  bank_account: '#3b82f6',
  investment:   '#8b5cf6',
  crypto:       '#f59e0b',
  real_estate:  '#06b6d4',
  liability:    '#ef4444',
  gold:         '#d97706',
  silver:       '#64748b',
  custom:       '#6b7280',
};

// ── Period helpers ─────────────────────────────────────────────────────────────

type PeriodMode = 'total' | 'weekly' | 'monthly' | 'annually' | 'custom';

function toDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function getWeekBounds(anchor: Date) {
  const d   = new Date(anchor);
  const day = d.getDay();
  const mon = new Date(d);
  mon.setDate(d.getDate() - ((day + 6) % 7));
  const sun = new Date(mon);
  sun.setDate(mon.getDate() + 6);
  const fmtShort = (dt: Date) =>
    dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const label = `${fmtShort(mon)} – ${fmtShort(sun)}, ${sun.getFullYear()}`;
  return { from: toDateStr(mon), to: toDateStr(sun), label };
}

function getMonthBounds(anchor: Date) {
  const y    = anchor.getFullYear();
  const m    = anchor.getMonth();
  const from = new Date(y, m, 1);
  const to   = new Date(y, m + 1, 0);
  const label = anchor.toLocaleString(undefined, { month: 'long', year: 'numeric' });
  return { from: toDateStr(from), to: toDateStr(to), label };
}

function getYearBounds(anchor: Date) {
  const y = anchor.getFullYear();
  return { from: `${y}-01-01`, to: `${y}-12-31`, label: String(y) };
}

function getPeriodBounds(mode: PeriodMode, anchor: Date, customFrom: string, customTo: string) {
  if (mode === 'total')    return { from: '2020-01-01', to: toDateStr(new Date()), label: 'All Time' };
  if (mode === 'weekly')   return getWeekBounds(anchor);
  if (mode === 'monthly')  return getMonthBounds(anchor);
  if (mode === 'annually') return getYearBounds(anchor);
  const label = customFrom && customTo ? `${customFrom} – ${customTo}` : 'Pick a range';
  return { from: customFrom, to: customTo, label };
}

function shiftAnchor(anchor: Date, mode: PeriodMode, dir: 1 | -1): Date {
  const d = new Date(anchor);
  if (mode === 'weekly')   d.setDate(d.getDate() + dir * 7);
  if (mode === 'monthly')  d.setMonth(d.getMonth() + dir);
  if (mode === 'annually') d.setFullYear(d.getFullYear() + dir);
  return d;
}

// Returns true if a 'YYYY-MM' month string falls within a from/to date range
function monthInRange(monthStr: string, from: string, to: string): boolean {
  return monthStr >= from.slice(0, 7) && monthStr <= to.slice(0, 7);
}

// ── Icons ──────────────────────────────────────────────────────────────────────
function ChevronLeftIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
    </svg>
  );
}
function ChevronRightIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  );
}

function useDarkMode() {
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'));
  useEffect(() => {
    const obs = new MutationObserver(() =>
      setDark(document.documentElement.classList.contains('dark'))
    );
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);
  return dark;
}

function fmtAxisTick(v: number): string {
  const abs = Math.abs(v);
  if (abs >= 1_000_000_000) return `${+(v / 1_000_000_000).toPrecision(3)}B`;
  if (abs >= 1_000_000)     return `${+(v / 1_000_000).toPrecision(3)}M`;
  if (abs >= 1_000)         return `${+(v / 1_000).toPrecision(3)}K`;
  return String(v);
}

const MODE_LABELS: Record<PeriodMode, string> = {
  total: 'Total', weekly: 'Weekly', monthly: 'Monthly', annually: 'Annually', custom: 'Custom',
};

export default function Dashboard() {
  const { user } = useAuth();
  const currency = user?.settings.baseCurrency ?? 'VND';
  const dark = useDarkMode();

  // ── Period state ────────────────────────────────────────────────────────────
  const now = new Date();
  const [mode,       setMode]       = useState<PeriodMode>('total');
  const [anchor,     setAnchor]     = useState<Date>(now);
  const [customFrom, setCustomFrom] = useState(toDateStr(now));
  const [customTo,   setCustomTo]   = useState(toDateStr(now));

  const { from, to, label: periodLabel } = getPeriodBounds(mode, anchor, customFrom, customTo);

  // ── Static data (no period dependency) ─────────────────────────────────────
  const [netWorth, setNetWorth]     = useState<NetWorthPoint[]>([]);
  const [monthly, setMonthly]       = useState<MonthlySummary[]>([]);
  const [assets, setAssets]         = useState<Asset[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [assetHistory, setAssetHistory] = useState<AssetBalanceHistory[]>([]);
  const [staticLoading, setStaticLoading] = useState(true);  // Gold portfolio P&L
  const [goldPnl, setGoldPnl] = useState<GoldPnlBreakdown | null>(null);
  const [goldPnlExpanded, setGoldPnlExpanded] = useState(false);
  // Silver portfolio P&L
  const [silverPnl, setSilverPnl] = useState<SilverPnlBreakdown | null>(null);
  const [silverPnlExpanded, setSilverPnlExpanded] = useState(false);
  const [hiddenExpenseSlices, setHiddenExpenseSlices] = useState<Set<string>>(new Set());
  const [hiddenAllocSlices, setHiddenAllocSlices] = useState<Set<string>>(new Set());
  // Bank saving contracts
  const [bankSavingContracts, setBankSavingContracts] = useState<{ asset: Asset; contracts: BankSavingContract[] }[]>([]);
  const [bankSavingExpanded, setBankSavingExpanded] = useState(false);
  useEffect(() => {
    Promise.all([
      summaryApi.netWorth(),
      summaryApi.monthly(),
      assetApi.list(),
      summaryApi.assetHistory(),
    ])
      .then(([nw, mo, a, ah]) => {
        setNetWorth(nw);
        setMonthly(mo);
        setAssets(a);
        setAssetHistory(ah);
        categoryApi.list().then(setCategories).catch(() => {});
        // Fetch gold portfolio P&L only if the user has gold assets
        if (a.some((asset) => asset.type === 'gold')) {
          goldApi.getPortfolioPnl().then(setGoldPnl).catch(() => {});
        }
        // Fetch silver portfolio P&L only if the user has silver assets
        if (a.some((asset) => asset.type === 'silver')) {
          silverApi.getPortfolioPnl().then(setSilverPnl).catch(() => {});
        }
        // Fetch bank saving contracts for all bank_saving assets
        const bankSavingAssets = a.filter((asset) => asset.type === 'bank_saving');
        if (bankSavingAssets.length > 0) {
          Promise.all(
            bankSavingAssets.map((asset) =>
              bankSavingApi.listContracts(asset.id)
                .then((contracts) => ({ asset, contracts: contracts.filter((c) => c.status !== 'closed') }))
                .catch(() => ({ asset, contracts: [] }))
            )
          ).then(setBankSavingContracts);
        }
      })
      .catch(console.error)
      .finally(() => setStaticLoading(false));
  }, []);

  // ── Period-scoped data ──────────────────────────────────────────────────────
  const [txns, setTxns]                   = useState<Transaction[]>([]);
  const [rangeSummary, setRangeSummary]   = useState<RangeSummary | null>(null);
  const [periodLoading, setPeriodLoading] = useState(true);

  useEffect(() => {
    if (!from || !to) return;
    setPeriodLoading(true);
    Promise.all([
      transactionApi.listRange(from, to),
      summaryApi.range(from, to),
    ])
      .then(([t, rs]) => {
        setTxns(t);
        setRangeSummary(rs);
      })
      .catch(console.error)
      .finally(() => setPeriodLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to]);

  const loading = staticLoading || periodLoading;

  const currentNetWorth = netWorth[netWorth.length - 1]?.netWorth ?? 0;
  const income          = rangeSummary?.income  ?? 0;
  const expense         = rangeSummary?.expense ?? 0;
  const net             = income - expense;
  const savingsRate     = income > 0 ? Math.round((net / income) * 100) : null;

  // Group non-liability assets by type for allocation chart.
  // Gold/Silver assets: balance is in quantity units, so use PnL currentValue (VND) instead.
  const assetAlloc = Object.entries(
    assets
      .filter((a) => !a.isLiability)
      .reduce<Record<string, number>>((acc, a) => {
        let value: number;
        if (a.type === 'gold') {
          value = goldPnl?.summary.currentValue ?? 0;
        } else if (a.type === 'silver') {
          value = silverPnl?.summary.currentValue ?? 0;
        } else {
          value = a.balance;
        }
        acc[a.type] = (acc[a.type] ?? 0) + value;
        return acc;
      }, {})
  ).map(([type, value]) => ({ name: type.replace('_', ' '), type, value }));

  // Expense pie grouped by category
  const catById = new Map(categories.map((cat) => [cat.id, cat]));
  const expensePie = Object.entries(
    txns
      .filter((t) => t.type === 'expense')
      .reduce<Record<string, number>>((acc, t) => {
        acc[t.categoryId] = (acc[t.categoryId] ?? 0) + t.amount;
        return acc;
      }, {})
  )
    .map(([categoryId, value]) => {
      const cat = catById.get(categoryId);
      return {
        name:  cat ? `${cat.icon ? `${cat.icon} ` : ''}${cat.name}` : 'Uncategorized',
        color: cat?.color ?? '#9ca3af',
        value,
      };
    })
    .sort((a, b) => b.value - a.value)
    .slice(0, 10);
  const expenseTotal = expensePie.reduce((s, d) => s + d.value, 0);

  // Recharts theme tokens
  const gridColor    = dark ? '#374151' : '#e5e7eb';
  const tickColor    = dark ? '#9ca3af' : '#6b7280';
  const tooltipStyle: { [key: string]: string | number } = {
    background:   dark ? '#1f2937' : '#ffffff',
    border:       `1px solid ${dark ? '#374151' : '#e5e7eb'}`,
    borderRadius: 8,
    fontSize:     12,
    color:        dark ? '#f3f4f6' : '#111827',
  };

  const allocTotal = assetAlloc.reduce((s, d) => s + d.value, 0);

  // Filter time-series charts to the selected period
  const filteredNetWorth  = netWorth.filter((p) => monthInRange(p.month, from, to));
  const filteredMonthly   = monthly.filter((p) => monthInRange(p.month, from, to));
  const filteredAssetHistory = assetHistory.map((a) => ({
    ...a,
    points: a.points.filter((p) => monthInRange(p.month, from, to)),
  })).filter((a) => a.points.length > 0);

  function makePieTooltip(total: number) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return function PieTooltip({ active, payload }: any) {
      if (!active || !payload?.length) return null;
      const entry = payload[0];
      const name  = entry.name as string;
      const value = entry.value as number;
      const fill  = entry.payload?.fill as string;
      const pct   = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
      return (
        <div style={tooltipStyle} className="px-3 py-2 shadow-lg rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <span style={{ background: fill }} className="inline-block h-2.5 w-2.5 rounded-full flex-shrink-0" />
            <span className="font-medium text-xs leading-snug max-w-[160px] break-words capitalize">{name}</span>
          </div>
          <div className="flex items-baseline justify-between gap-4 text-xs">
            <span style={{ color: dark ? '#9ca3af' : '#6b7280' }}>Amount</span>
            <span className="font-mono font-semibold">{fmt(value, currency)}</span>
          </div>
          <div className="flex items-baseline justify-between gap-4 text-xs mt-0.5">
            <span style={{ color: dark ? '#9ca3af' : '#6b7280' }}>Share</span>
            <span className="font-mono font-semibold">{pct}%</span>
          </div>
        </div>
      );
    };
  }

  if (loading && staticLoading) return <div className="p-4 sm:p-6"><Spinner /></div>;

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      {/* ── Header with period selector ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Dashboard</h1>

        <div className="flex flex-wrap items-center gap-2">
          {/* Period mode tabs */}
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value as PeriodMode)}
            className="sm:hidden rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 shadow-sm outline-none"
          >
            {(Object.keys(MODE_LABELS) as PeriodMode[]).map((m) => (
              <option key={m} value={m}>{MODE_LABELS[m]}</option>
            ))}
          </select>
          <div className="hidden sm:flex rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
            {(Object.keys(MODE_LABELS) as PeriodMode[]).map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  mode === m
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {MODE_LABELS[m]}
              </button>
            ))}
          </div>

          {/* Navigator */}
          {mode !== 'custom' && mode !== 'total' && (
            <div className="flex items-center gap-0.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
              <button
                onClick={() => setAnchor((a) => shiftAnchor(a, mode, -1))}
                className="rounded-l-xl p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <ChevronLeftIcon />
              </button>
              <span className="min-w-[100px] sm:min-w-[120px] px-1 sm:px-2 text-center text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-200">
                {periodLabel}
              </span>
              <button
                onClick={() => setAnchor((a) => shiftAnchor(a, mode, 1))}
                className="rounded-r-xl p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <ChevronRightIcon />
              </button>
            </div>
          )}

          {/* Custom date range */}
          {mode === 'custom' && (
            <div className="flex items-center gap-1.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-1.5 shadow-sm">
              <input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="bg-transparent text-sm text-gray-700 dark:text-gray-200 outline-none dark:[color-scheme:dark]"
              />
              <span className="text-gray-400 dark:text-gray-600">–</span>
              <input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className="bg-transparent text-sm text-gray-700 dark:text-gray-200 outline-none dark:[color-scheme:dark]"
              />
            </div>
          )}
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 sm:grid-cols-4">
        <StatCard label="Net Worth"     value={fmt(currentNetWorth, currency)} accent="blue"   />
        <StatCard label="Income"        sub={periodLabel} value={fmt(income, currency)}  accent="green"  />
        <StatCard label="Expenses"      sub={periodLabel} value={fmt(expense, currency)} accent="red"    />
        <StatCard
          label="Savings Rate"
          sub={periodLabel}
          value={savingsRate !== null ? `${savingsRate}%` : '—'}
          accent={savingsRate !== null && savingsRate >= 0 ? 'purple' : 'red'}
        />
      </div>

      {/* Gold + Silver Investment Row */}
      {(goldPnl || silverPnl) && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">

          {/* Gold Investment */}
          {goldPnl && (
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => setGoldPnlExpanded((v) => !v)}
                className="w-full text-left rounded-xl border-2 border-amber-300 dark:border-amber-600 bg-amber-100 dark:bg-amber-900/40 shadow-sm ring-1 ring-amber-100 dark:ring-amber-800/30 focus:outline-none"
              >
                <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
                  <div>
                    <p className="text-xs font-semibold text-amber-700 dark:text-amber-300 uppercase tracking-wider">🥇 Total Gold Investment</p>
                    <p className="mt-0.5 text-sm font-bold text-amber-900 dark:text-amber-200">
                      {goldPnl.summary.totalQuantity.toLocaleString()} chỉ • Cost {goldPnl.summary.totalCost.toLocaleString('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 })} • Value {goldPnl.summary.currentValue.toLocaleString('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 })}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-base font-bold tabular-nums ${
                      goldPnl.summary.unrealizedPnl >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'
                    }`}>
                      {goldPnl.summary.unrealizedPnl >= 0 ? '+' : ''}{goldPnl.summary.unrealizedPnl.toLocaleString('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 })}
                      &nbsp;({goldPnl.summary.returnPercent >= 0 ? '+' : ''}{goldPnl.summary.returnPercent.toFixed(2)}%)
                    </span>
                    {goldPnl.breakdown.length > 0 && (
                      <svg className={`h-4 w-4 text-amber-600 dark:text-amber-400 transition-transform ${goldPnlExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    )}
                  </div>
                </div>
              </button>

              {goldPnlExpanded && goldPnl.breakdown.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {goldPnl.breakdown.map((item) => (
                    <div key={`${item.merchantId}:${item.goldType}`} className="rounded-xl border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 shadow-sm">
                      <div className="flex items-center justify-between px-4 py-2.5 border-b border-amber-100 dark:border-amber-800">
                        <p className="text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wider">
                          {item.merchantName} • {item.goldType === 'bar' ? 'Miếng' : 'Nhẫn'}
                        </p>
                        <span className={`text-xs font-bold tabular-nums ${
                          item.unrealizedPnl >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'
                        }`}>
                          {item.unrealizedPnl >= 0 ? '+' : ''}{item.unrealizedPnl.toLocaleString('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 })}
                          &nbsp;({item.returnPercent >= 0 ? '+' : ''}{item.returnPercent.toFixed(2)}%)
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-3 px-4 py-3">
                        <div>
                          <p className="text-xs text-amber-600 dark:text-amber-500">Quantity</p>
                          <p className="mt-0.5 text-sm font-bold text-amber-900 dark:text-amber-200">{item.totalQuantity.toLocaleString()} chỉ</p>
                        </div>
                        <div>
                          <p className="text-xs text-amber-600 dark:text-amber-500">Sell Price</p>
                          <p className="mt-0.5 text-sm font-semibold text-amber-800 dark:text-amber-300">
                            {item.currentSellPrice.toLocaleString('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 })} / chỉ
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-amber-600 dark:text-amber-500">Cost</p>
                          <p className="mt-0.5 text-sm font-semibold text-amber-800 dark:text-amber-300">
                            {item.totalCost.toLocaleString('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 })}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-amber-600 dark:text-amber-500">Value</p>
                          <p className="mt-0.5 text-sm font-semibold text-amber-800 dark:text-amber-300">
                            {item.currentValue.toLocaleString('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 })}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Silver Investment */}
          {silverPnl && (
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => setSilverPnlExpanded((v) => !v)}
                className="w-full text-left rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-900/40 shadow-sm ring-1 ring-slate-100 dark:ring-slate-800/30 focus:outline-none"
              >
                <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
                  <div>
                    <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">🥈 Total Silver Investment</p>
                    <p className="mt-0.5 text-sm font-bold text-slate-900 dark:text-slate-200">
                      {silverPnl.summary.totalQuantity.toLocaleString()} lượng • Cost {silverPnl.summary.totalCost.toLocaleString('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 })} • Value {silverPnl.summary.currentValue.toLocaleString('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 })}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-base font-bold tabular-nums ${
                      silverPnl.summary.unrealizedPnl >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'
                    }`}>
                      {silverPnl.summary.unrealizedPnl >= 0 ? '+' : ''}{silverPnl.summary.unrealizedPnl.toLocaleString('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 })}
                      &nbsp;({silverPnl.summary.returnPercent >= 0 ? '+' : ''}{silverPnl.summary.returnPercent.toFixed(2)}%)
                    </span>
                    {silverPnl.breakdown.length > 0 && (
                      <svg className={`h-4 w-4 text-slate-600 dark:text-slate-400 transition-transform ${silverPnlExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    )}
                  </div>
                </div>
              </button>

              {silverPnlExpanded && silverPnl.breakdown.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {silverPnl.breakdown.map((item) => (
                    <div key={`${item.merchantId}:${item.silverType}`} className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/20 shadow-sm">
                      <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 dark:border-slate-800">
                        <p className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                          {item.merchantName} • {item.silverType === 'bar' ? 'Bạc Miếng' : 'Bạc 1kg'}
                        </p>
                        <span className={`text-xs font-bold tabular-nums ${
                          item.unrealizedPnl >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'
                        }`}>
                          {item.unrealizedPnl >= 0 ? '+' : ''}{item.unrealizedPnl.toLocaleString('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 })}
                          &nbsp;({item.returnPercent >= 0 ? '+' : ''}{item.returnPercent.toFixed(2)}%)
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-3 px-4 py-3">
                        <div>
                          <p className="text-xs text-slate-600 dark:text-slate-500">Quantity</p>
                          <p className="mt-0.5 text-sm font-bold text-slate-900 dark:text-slate-200">{item.totalQuantity.toLocaleString()} {item.silverType === 'bar1kg' ? 'kg' : 'lượng'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-600 dark:text-slate-500">Sell Price</p>
                          <p className="mt-0.5 text-sm font-semibold text-slate-800 dark:text-slate-300">
                            {item.currentSellPrice.toLocaleString('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 })} / {item.silverType === 'bar1kg' ? 'kg' : 'lượng'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-600 dark:text-slate-500">Cost</p>
                          <p className="mt-0.5 text-sm font-semibold text-slate-800 dark:text-slate-300">
                            {item.totalCost.toLocaleString('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 })}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-600 dark:text-slate-500">Value</p>
                          <p className="mt-0.5 text-sm font-semibold text-slate-800 dark:text-slate-300">
                            {item.currentValue.toLocaleString('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 })}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

        </div>
      )}

      {/* Bank Saving Overview */}
      {bankSavingContracts.length > 0 && (() => {
        const allContracts = bankSavingContracts.flatMap((g) => g.contracts);
        const totalPrincipal = allContracts.reduce((s, c) => s + c.remainingPrincipal, 0);
        const totalEndValue  = allContracts.reduce((s, c) => s + c.estimatedEndValue, 0);
        const totalInterest  = totalEndValue - totalPrincipal;
        const activeCount    = allContracts.filter((c) => c.status === 'active').length;
        const partialCount   = allContracts.filter((c) => c.status === 'partial').length;

        // Days until next maturity
        const today = new Date();
        const upcoming = allContracts
          .map((c) => ({ ...c, daysLeft: Math.ceil((new Date(c.estimatedEndDate).getTime() - today.getTime()) / 86_400_000) }))
          .filter((c) => c.daysLeft >= 0)
          .sort((a, b) => a.daysLeft - b.daysLeft)[0];

        return (
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => setBankSavingExpanded((v) => !v)}
              className="w-full text-left rounded-xl border-2 border-teal-300 dark:border-teal-600 bg-teal-50 dark:bg-teal-900/30 shadow-sm ring-1 ring-teal-100 dark:ring-teal-800/30 focus:outline-none"
            >
              <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
                <div>
                  <p className="text-xs font-semibold text-teal-700 dark:text-teal-300 uppercase tracking-wider">
                    🏦 Bank Saving Overview
                  </p>
                  <p className="mt-0.5 text-sm font-bold text-teal-900 dark:text-teal-200">
                    {allContracts.length} contract{allContracts.length !== 1 ? 's' : ''}
                    {activeCount > 0 && ` · ${activeCount} active`}
                    {partialCount > 0 && ` · ${partialCount} partial`}
                    {' · '}Principal {fmt(totalPrincipal, currency)}
                    {' · '}Est. value {fmt(totalEndValue, currency)}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {upcoming && (
                    <span className={`text-xs font-medium tabular-nums px-2 py-0.5 rounded-full ${
                      upcoming.daysLeft <= 30
                        ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300'
                        : 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300'
                    }`}>
                      Next maturity {upcoming.daysLeft === 0 ? 'today' : `in ${upcoming.daysLeft}d`}
                    </span>
                  )}
                  <span className="text-sm font-bold tabular-nums text-teal-600 dark:text-teal-400">
                    +{fmt(totalInterest, currency)} interest
                  </span>
                  <svg className={`h-4 w-4 text-teal-600 dark:text-teal-400 transition-transform ${bankSavingExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </button>

            {bankSavingExpanded && (
              <div className="space-y-3">
                {bankSavingContracts.map(({ asset, contracts }) => (
                  contracts.length === 0 ? null : (
                    <div key={asset.id}>
                      <p className="mb-2 px-1 text-xs font-semibold text-teal-700 dark:text-teal-400 uppercase tracking-wider">{asset.name}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {contracts.map((c) => {
                          const daysLeft = Math.ceil((new Date(c.estimatedEndDate).getTime() - today.getTime()) / 86_400_000);
                          const interest  = c.estimatedEndValue - c.remainingPrincipal;
                          const isOverdue = daysLeft < 0;
                          return (
                            <div key={c.id} className={`rounded-xl shadow-sm p-3.5 space-y-2 ${
                              isOverdue
                                ? 'border-2 border-orange-400 dark:border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                                : 'border border-teal-200 dark:border-teal-700 bg-white dark:bg-gray-800'
                            }`}>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-1.5 min-w-0">
                                  <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{c.bankName}</span>
                                  {isOverdue && <span className="text-orange-500 dark:text-orange-400 text-xs shrink-0">⚠</span>}
                                </div>
                                <span className={`rounded-full px-2 py-0.5 text-xs font-medium shrink-0 ${
                                  isOverdue
                                    ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300'
                                    : c.status === 'active'
                                      ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300'
                                      : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300'
                                }`}>{isOverdue ? 'overdue' : c.status}</span>
                              </div>
                              <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                                <div>
                                  <p className="text-teal-600 dark:text-teal-500">Principal</p>
                                  <p className="font-semibold text-gray-800 dark:text-gray-200">{fmt(c.remainingPrincipal, c.currency)}</p>
                                </div>
                                <div>
                                  <p className="text-teal-600 dark:text-teal-500">Rate</p>
                                  <p className="font-semibold text-gray-800 dark:text-gray-200">{c.interestRate}% · {c.periodMonths}m</p>
                                </div>
                                <div>
                                  <p className="text-teal-600 dark:text-teal-500">Est. Interest</p>
                                  <p className="font-semibold text-emerald-600 dark:text-emerald-400">+{fmt(interest, c.currency)}</p>
                                </div>
                                <div>
                                  <p className="text-teal-600 dark:text-teal-500">Matures</p>
                                  <p className={`font-semibold ${
                                    isOverdue
                                      ? 'text-orange-600 dark:text-orange-400'
                                      : daysLeft <= 30
                                        ? 'text-orange-500 dark:text-orange-300'
                                        : 'text-gray-800 dark:text-gray-200'
                                  }`}>
                                    {c.estimatedEndDate}
                                    {isOverdue ? ` (${Math.abs(daysLeft)}d ago)` : ` (${daysLeft}d)`}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* Charts */}
      <div className="grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-2">

        {/* Net Worth area chart */}
        <ChartCard title="Net Worth Over Time">
          {filteredNetWorth.length === 0 ? <EmptyState message="No net worth data for this period" /> : (
            <ResponsiveContainer width="100%" height={230}>
              <AreaChart data={filteredNetWorth} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="nwGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#3b82f6" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}   />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: tickColor }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: tickColor }} axisLine={false} tickLine={false} tickFormatter={fmtAxisTick} />
                <Tooltip
                  formatter={(v: number) => [fmt(v, currency), 'Net Worth']}
                  contentStyle={tooltipStyle}
                />
                <Area
                  type="monotone"
                  dataKey="netWorth"
                  stroke="#3b82f6"
                  strokeWidth={2.5}
                  fill="url(#nwGrad)"
                  dot={false}
                  activeDot={{ r: 4, fill: '#3b82f6' }}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        {/* Monthly cash flow */}
        <ChartCard title="Cash Flow">
          {filteredMonthly.length === 0 ? <EmptyState message="No cash flow data for this period" /> : (
            <ResponsiveContainer width="100%" height={230}>
              <BarChart data={filteredMonthly} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: tickColor }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: tickColor }} axisLine={false} tickLine={false} tickFormatter={fmtAxisTick} />
                <Tooltip
                  formatter={(v: number, name: string) => [fmt(v, currency), name]}
                  contentStyle={tooltipStyle}
                />
                <Legend wrapperStyle={{ fontSize: 12, color: tickColor }} />
                <Bar dataKey="income"  name="Income"  fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={28} />
                <Bar dataKey="expense" name="Expense" fill="#ef4444" radius={[4, 4, 0, 0]} maxBarSize={28} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        {/* Expenses by category — donut */}
        <ChartCard title="Expenses by Category">
          {expensePie.length === 0 ? (
            <EmptyState message="No expense data for this period" />
          ) : (() => {
            const visibleTotal: number = expensePie
              .filter((d) => !hiddenExpenseSlices.has(d.name))
              .reduce((s: number, d) => s + d.value, 0);

            function toggleSlice(name: string) {
              setHiddenExpenseSlices((prev) => {
                const next = new Set(prev);
                if (next.has(name)) next.delete(name); else next.add(name);
                return next;
              });
            }

            return (
              <div className="flex flex-col">
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={expensePie}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={52}
                      outerRadius={80}
                      paddingAngle={2}
                      onClick={(data: { name: string }) => toggleSlice(data.name)}
                      style={{ cursor: 'pointer' }}
                    >
                      {expensePie.map((entry, i) => (
                        <Cell
                          key={i}
                          fill={entry.color}
                          stroke="none"
                          opacity={hiddenExpenseSlices.has(entry.name) ? 0.15 : 1}
                        />
                      ))}
                    </Pie>
                    <Tooltip content={makePieTooltip(visibleTotal)} />
                  </PieChart>
                </ResponsiveContainer>

                {/* Scrollable legend table */}
                <div className="overflow-y-auto max-h-40 border-t border-gray-100 dark:border-gray-700 mt-1">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-white dark:bg-gray-800 z-10">
                      <tr className="text-gray-400 dark:text-gray-500">
                        <th className="text-left font-medium pb-1.5 pt-2 pr-2">Category</th>
                        <th className="text-right font-medium pb-1.5 pt-2 pr-2">Value</th>
                        <th className="text-right font-medium pb-1.5 pt-2">Share</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                      {expensePie.map((entry) => {
                        const isHidden = hiddenExpenseSlices.has(entry.name);
                        const pct = expenseTotal > 0
                          ? ((entry.value / expenseTotal) * 100).toFixed(1)
                          : '0.0';
                        return (
                          <tr
                            key={entry.name}
                            onClick={() => toggleSlice(entry.name)}
                            className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors select-none ${
                              isHidden ? 'opacity-35' : ''
                            }`}
                          >
                            <td className="py-1.5 pr-2">
                              <div className="flex items-center gap-1.5">
                                <span
                                  className="inline-block h-2 w-2 shrink-0 rounded-full"
                                  style={{ background: entry.color }}
                                />
                                <span className="truncate max-w-[140px] text-gray-700 dark:text-gray-300">{entry.name}</span>
                              </div>
                            </td>
                            <td className="py-1.5 pr-2 text-right tabular-nums font-medium text-gray-800 dark:text-gray-200">
                              {fmt(entry.value as number, currency)}
                            </td>
                            <td className="py-1.5 text-right tabular-nums text-gray-500 dark:text-gray-400">
                              {pct}%
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}
        </ChartCard>

        {/* Asset allocation — donut */}
        <ChartCard title="Asset Allocation">
          {assetAlloc.length === 0 ? (
            <EmptyState message="No assets to display" />
          ) : (() => {
            const visibleAllocTotal: number = assetAlloc
              .filter((d) => !hiddenAllocSlices.has(d.name))
              .reduce((s: number, d) => s + d.value, 0);

            function toggleAllocSlice(name: string) {
              setHiddenAllocSlices((prev) => {
                const next = new Set(prev);
                if (next.has(name)) next.delete(name); else next.add(name);
                return next;
              });
            }

            return (
              <div className="flex flex-col">
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={assetAlloc}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={52}
                      outerRadius={80}
                      paddingAngle={2}
                      onClick={(data: { name: string }) => toggleAllocSlice(data.name)}
                      style={{ cursor: 'pointer' }}
                    >
                      {assetAlloc.map((entry, i) => (
                        <Cell
                          key={i}
                          fill={ASSET_TYPE_COLORS[entry.type] ?? CHART_COLORS[i % CHART_COLORS.length]}
                          stroke="none"
                          opacity={hiddenAllocSlices.has(entry.name) ? 0.15 : 1}
                        />
                      ))}
                    </Pie>
                    <Tooltip content={makePieTooltip(visibleAllocTotal)} />
                  </PieChart>
                </ResponsiveContainer>

                {/* Scrollable legend table */}
                <div className="overflow-y-auto max-h-40 border-t border-gray-100 dark:border-gray-700 mt-1">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-white dark:bg-gray-800 z-10">
                      <tr className="text-gray-400 dark:text-gray-500">
                        <th className="text-left font-medium pb-1.5 pt-2 pr-2">Asset</th>
                        <th className="text-right font-medium pb-1.5 pt-2 pr-2">Value</th>
                        <th className="text-right font-medium pb-1.5 pt-2">Share</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                      {assetAlloc.map((entry, i) => {
                        const isHidden = hiddenAllocSlices.has(entry.name);
                        const pct = allocTotal > 0
                          ? ((entry.value / allocTotal) * 100).toFixed(1)
                          : '0.0';
                        const color = ASSET_TYPE_COLORS[entry.type] ?? CHART_COLORS[i % CHART_COLORS.length];
                        return (
                          <tr
                            key={entry.name}
                            onClick={() => toggleAllocSlice(entry.name)}
                            className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors select-none ${
                              isHidden ? 'opacity-35' : ''
                            }`}
                          >
                            <td className="py-1.5 pr-2">
                              <div className="flex items-center gap-1.5">
                                <span
                                  className="inline-block h-2 w-2 shrink-0 rounded-full"
                                  style={{ background: color }}
                                />
                                <span className="truncate max-w-[140px] text-gray-700 dark:text-gray-300">{entry.name}</span>
                              </div>
                            </td>
                            <td className="py-1.5 pr-2 text-right tabular-nums font-medium text-gray-800 dark:text-gray-200">
                              {fmt(entry.value as number, currency)}
                            </td>
                            <td className="py-1.5 text-right tabular-nums text-gray-500 dark:text-gray-400">
                              {pct}%
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}
        </ChartCard>

        {/* Per-asset balance over time — full width */}
        <ChartCard title="Asset Balances" className="lg:col-span-2">
          {filteredAssetHistory.length === 0 || filteredAssetHistory.every((a) => a.points.every((p) => p.balance === 0)) ? (
            <EmptyState message="No asset balance data for this period" />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart
                data={(() => {
                  // Merge all assets' filtered points into a single array keyed by month
                  const months = filteredAssetHistory[0]?.points.map((p) => p.month) ?? [];
                  return months.map((month, i) => {
                    const row: Record<string, number | string> = { month };
                    for (const a of filteredAssetHistory) {
                      row[a.assetName] = a.points[i]?.balance ?? 0;
                    }
                    return row;
                  });
                })()}
                margin={{ top: 4, right: 4, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: tickColor }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: tickColor }} axisLine={false} tickLine={false} tickFormatter={fmtAxisTick} />
                <Tooltip
                  formatter={(v: number, name: string) => [fmt(v, currency), name]}
                  contentStyle={tooltipStyle}
                />
                <Legend wrapperStyle={{ fontSize: 12, color: tickColor }} />
                {filteredAssetHistory.map((a, i) => (
                  <Line
                    key={a.assetId}
                    type="monotone"
                    dataKey={a.assetName}
                    stroke={CHART_COLORS[i % CHART_COLORS.length]}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

      </div>
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

const ACCENT: Record<string, { border: string; text: string }> = {
  blue:   { border: 'border-l-blue-500',    text: 'text-blue-600 dark:text-blue-400'    },
  green:  { border: 'border-l-emerald-500', text: 'text-emerald-600 dark:text-emerald-400' },
  red:    { border: 'border-l-red-500',     text: 'text-red-500 dark:text-red-400'      },
  purple: { border: 'border-l-purple-500',  text: 'text-purple-600 dark:text-purple-400' },
};

function StatCard({ label, sub, value, accent }: { label: string; sub?: string; value: string; accent: string }) {
  const cls = ACCENT[accent] ?? ACCENT['blue']!;
  return (
    <div className={`rounded-xl border-l-4 ${cls.border} bg-white dark:bg-gray-800 p-3 sm:p-4 shadow-sm ring-1 ring-gray-100 dark:ring-gray-700/50`}>
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 truncate">
        {label}
        {sub && <span className="ml-1 hidden sm:inline font-normal normal-case text-gray-400 dark:text-gray-500">· {sub}</span>}
      </p>
      <p className={`mt-1 sm:mt-1.5 text-lg sm:text-xl font-bold tabular-nums truncate ${cls.text}`}>{value}</p>
    </div>
  );
}

function ChartCard({ title, children, className = '' }: { title: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={`rounded-xl bg-white dark:bg-gray-800 p-5 shadow-sm ring-1 ring-gray-100 dark:ring-gray-700/50 ${className}`}>
      <h2 className="mb-4 text-sm font-semibold text-gray-700 dark:text-gray-300">{title}</h2>
      {children}
    </div>
  );
}

function Spinner() {
  return (
    <div className="flex h-40 items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-[3px] border-primary-600 border-t-transparent" />
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <p className="flex h-[230px] items-center justify-center text-sm text-gray-400 dark:text-gray-500">
      {message}
    </p>
  );
}

function fmt(value: number, currency: string) {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value);
}
